import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box, Typography, TextField, Button, Alert, CircularProgress, Card, CardContent,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { PhoneInput, validatePhoneNumber } from '../../components/PhoneInput';
import {
  getMyCustomerProfile, updateMyCustomerProfile, uploadMyDocument, deleteMyDocument, CustomerProfile,
} from '../../api/customerProfile';
import { PageHeader } from '../../components/ui/PageHeader';
import { ErrorRetry } from '../../components/ui/ErrorRetry';
import { DocumentUploadZone } from '../../components/tracking/DocumentUploadZone';


interface FormState {
  fullName: string;
  phoneNumber: string;
  defaultDeliveryAddress: string;
}

function toForm(p: CustomerProfile): FormState {
  return {
    fullName: p.fullName ?? '',
    phoneNumber: p.phoneNumber ?? '',
    defaultDeliveryAddress: p.defaultDeliveryAddress ?? '',
  };
}

export const MyProfile: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState | null>(null);
  const [snapshot, setSnapshot] = useState<FormState | null>(null);
  const [phoneError, setPhoneError] = useState('');
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const profileQuery = useQuery({
    queryKey: ['my-customer-profile'],
    queryFn: getMyCustomerProfile,
    retry: false,
  });

  useEffect(() => {
    if (profileQuery.data && form === null) {
      const next = toForm(profileQuery.data);
      setForm(next);
      setSnapshot(next);
    }
  }, [profileQuery.data, form]);

  // Auto-dismiss success alert after 3s
  useEffect(() => {
    if (!savedAt) return;
    const t = setTimeout(() => setSavedAt(null), 3000);
    return () => clearTimeout(t);
  }, [savedAt]);

  const mutation = useMutation({
    mutationFn: (input: { fullName: string; phoneNumber: string; defaultDeliveryAddress: string }) =>
      updateMyCustomerProfile(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-customer-profile'] });
      if (form) setSnapshot(form);
      setSavedAt(Date.now());
    },
  });

  if (profileQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress sx={{ color: 'text.headline' }} />
      </Box>
    );
  }

  if (profileQuery.isError) {
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', mt: { xs: 4, sm: 8 }, px: 3 }}>
        <PageHeader title="Mi perfil" />
        <ErrorRetry message="Error al cargar tu perfil." onRetry={() => profileQuery.refetch()} />
      </Box>
    );
  }

  if (!form) return null;

  const handleFullName = (v: string) => setForm({ ...form, fullName: v });
  const handlePhone = (v: string) => {
    setForm({ ...form, phoneNumber: v });
    setPhoneError(validatePhoneNumber(v) ?? '');
  };
  const handleAddress = (v: string) => setForm({ ...form, defaultDeliveryAddress: v });

  const isDirty = snapshot ? JSON.stringify(form) !== JSON.stringify(snapshot) : false;
  const fullNameOk = form.fullName.trim().length >= 2;
  const phoneOk = !validatePhoneNumber(form.phoneNumber);
  const canSave = isDirty && fullNameOk && phoneOk && !mutation.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSave) return;
    mutation.mutate({
      fullName: form.fullName.trim(),
      phoneNumber: form.phoneNumber,
      defaultDeliveryAddress: form.defaultDeliveryAddress.trim(),
    });
  };

  return (
    <Box sx={{ maxWidth: 480, mx: 'auto', mt: { xs: 4, sm: 8 }, px: 3, pb: 6 }}>
      <PageHeader title="Mi perfil" />

      <Card>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Correo
          </Typography>
          <Typography sx={{ color: 'text.headline' }}>{user?.email}</Typography>
        </Box>

        {savedAt && (
          <Alert severity="success" sx={{ mb: 2 }}>Cambios guardados</Alert>
        )}

        {mutation.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {(mutation.error as any)?.response?.data?.message ?? 'Error al guardar.'}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Nombre completo"
            value={form.fullName}
            onChange={(e) => handleFullName(e.target.value)}
            error={!!form.fullName && !fullNameOk}
            helperText={form.fullName && !fullNameOk ? 'Mínimo 2 caracteres' : undefined}
            sx={{ mb: 2 }}
            required
          />

          <Box sx={{ mb: 2 }}>
            <PhoneInput
              label="Teléfono"
              value={form.phoneNumber}
              onChange={handlePhone}
              error={!!phoneError}
              helperText={phoneError}
            />
          </Box>

          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Dirección por defecto"
            value={form.defaultDeliveryAddress}
            onChange={(e) => handleAddress(e.target.value)}
            sx={{ mb: 3 }}
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={!canSave}
            color="primary"
            sx={{ py: 1.5 }}
          >
            {mutation.isPending ? <CircularProgress size={20} color="inherit" /> : 'Guardar'}
          </Button>
        </form>
      </CardContent></Card>

      <Card sx={{ mt: 3 }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Typography
            component="span"
            sx={{
              fontFamily: '"Plus Jakarta Sans", sans-serif',
              fontSize: '0.68rem',
              fontWeight: 700,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: 'text.secondary',
              display: 'block',
              mb: 0.75,
            }}
          >
            Mis documentos
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Bricolage Grotesque", "Plus Jakarta Sans", sans-serif',
              fontSize: '1.25rem',
              fontWeight: 500,
              letterSpacing: '-0.015em',
              color: 'text.headline',
              lineHeight: 1.25,
              mb: 0.5,
            }}
          >
            Cédula y carnet de seguro
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5 }}>
            Súbelos una sola vez y los usaremos automáticamente en tus próximos pedidos.
          </Typography>

          <DocumentUploadZone
            label="Cédula"
            description="Imagen de tu cédula o pasaporte. JPG, PNG, WebP o PDF (máx. 10 MB)."
            initialUrl={profileQuery.data?.cedulaImageUrl}
            uploadFn={async (file) => {
              const res = await uploadMyDocument('CEDULA', file);
              queryClient.invalidateQueries({ queryKey: ['my-customer-profile'] });
              return { url: res.url };
            }}
            onDelete={async () => {
              await deleteMyDocument('CEDULA');
              queryClient.invalidateQueries({ queryKey: ['my-customer-profile'] });
            }}
          />

          <DocumentUploadZone
            label="Carnet de seguro"
            description="Imagen del frente de tu carnet de seguro médico."
            initialUrl={profileQuery.data?.insuranceCardImageUrl}
            uploadFn={async (file) => {
              const res = await uploadMyDocument('INSURANCE_CARD', file);
              queryClient.invalidateQueries({ queryKey: ['my-customer-profile'] });
              return { url: res.url };
            }}
            onDelete={async () => {
              await deleteMyDocument('INSURANCE_CARD');
              queryClient.invalidateQueries({ queryKey: ['my-customer-profile'] });
            }}
          />
        </CardContent>
      </Card>
    </Box>
  );
};
